import type { UsageMetric } from '../services/api'
import { Meter } from '../ds'

/**
 * One metered allowance as Account → Billing draws it: `used / limit`, gold (sage for emails),
 * danger at or past the limit, with " · at limit" or " · +240 overage · $12.00" after the count.
 *
 * Extracted from `screens/Account.tsx`'s `BillingPanel` (MAR-317, PR #321 review) so the
 * shell's profile-menu meter is the SAME rendering as the billing page, not a second one that
 * drifted — the menu's earlier meter was the dead `BillingView`'s (sage tone, the server's
 * `warning`, "Limit reached!"), which no live screen drew.
 */
export interface UsageMeterProps {
  label: string
  metric: UsageMetric
  /** Billing draws Emails in sage; everything else in gold. */
  sage?: boolean
}

export function usageMeterParts(metric: UsageMetric): { pct: number; atLimit: boolean; valueText: string } {
  const rawPct = Math.round(metric.percentage ?? (metric.limit ? (metric.used / metric.limit) * 100 : 0))
  const pct = Math.min(100, rawPct)
  const atLimit = rawPct >= 100
  const overageUsed = metric.overage?.used ?? 0
  const overageCharge = metric.overage?.chargeCents ?? 0
  const counted = `${metric.used.toLocaleString()} / ${metric.limit.toLocaleString()}`
  const flag =
    atLimit && overageUsed > 0
      ? ` · +${overageUsed.toLocaleString()} overage${overageCharge > 0 ? ` · $${(overageCharge / 100).toFixed(2)}` : ''}`
      : atLimit
      ? ' · at limit'
      : ''
  return { pct, atLimit, valueText: `${counted}${flag}` }
}

export default function UsageMeter({ label, metric, sage }: UsageMeterProps) {
  const { pct, atLimit, valueText } = usageMeterParts(metric)
  return <Meter label={label} value={pct} valueText={valueText} tone={atLimit ? 'danger' : sage ? 'sage' : 'gold'} />
}
